import { Schema } from "effect"
import { Evidence, RankedRef, WorkRef } from "../common"
import { AreaInterface, AreaPowers, Charter, Mind } from "../domain/org"
import { GoalSpec, Horizon, InitiativeShape, KpiSource, MetricCondition } from "../domain/plan"
import { DoneCheck, EdgeKind, Hands, ReviewConfig, TaskExecutor, TaskParent } from "../domain/task"
import {
  AgentId,
  AreaId,
  EdgeId,
  GoalId,
  Handle,
  InitiativeId,
  IntegrationId,
  IntentKey,
  KpiId,
  Markdown,
  Millis,
  PolicyId,
  Rank,
  Slug,
  TaskId,
  UserId,
} from "../ids"
import { Placement, PlacementDefaults } from "../placement"

// #region graph-commands
const opt = Schema.optionalKey

export const GoalPatch = Schema.Struct({
  title: opt(Schema.String),
  why: opt(Schema.String),
  body: opt(Markdown),
  deadline: opt(Schema.NullOr(Millis)),
  kpiIds: opt(Schema.Array(KpiId)),
  tags: opt(Schema.Array(Slug)),
})
export const KpiPatch = Schema.Struct({
  name: opt(Schema.String),
  unit: opt(Schema.String),
  role: opt(Schema.Literals(["input", "lagging", "health"])),
  source: opt(KpiSource),
  latencyDays: opt(Schema.Int),
  minReportable: opt(Schema.NullOr(Schema.Int)),
  rollsUpTo: opt(Schema.Array(KpiId)),
  baseline: opt(Schema.NullOr(Schema.Struct({ value: Schema.Finite, at: Millis }))),
})
export const AreaPatch = Schema.Struct({
  name: opt(Schema.String),
  color: opt(Schema.String),
  intakeAgentId: opt(Schema.NullOr(AgentId)),
  placement: opt(PlacementDefaults),
  powers: opt(AreaPowers),
  wip: opt(Schema.Struct({ maxActiveInitiatives: Schema.Int })),
  interfaces: opt(
    Schema.Struct({
      publishes: Schema.Array(AreaInterface),
      consumes: Schema.Array(Schema.Struct({ areaId: AreaId, key: Slug })),
    }),
  ),
})
export const AgentPatch = Schema.Struct({
  name: opt(Schema.String),
  title: opt(Schema.String),
  avatar: opt(Schema.Struct({ emoji: Schema.NullOr(Schema.String), color: Schema.String })),
  hands: opt(Hands),
  wake: opt(Schema.Struct({ debounceMs: Schema.Int })),
  areaId: opt(Schema.NullOr(AreaId)),
  /** Settings wave: unique among the project's agents and members. */
  handle: opt(Handle),
  /** Written only by TakeOverAgent (the AgentUpdated event shares this schema); UpdateAgent with it → Invalid. */
  ownerId: opt(UserId),
})
export const InitiativePatch = Schema.Struct({
  title: opt(Schema.String),
  why: opt(Schema.String),
  body: opt(Markdown),
  horizon: opt(Horizon),
  goalIds: opt(Schema.NonEmptyArray(GoalId)),
  ownerAgentId: opt(Schema.NullOr(AgentId)),
  shape: opt(InitiativeShape),
  tags: opt(Schema.Array(Slug)),
})
export const TaskPatch = Schema.Struct({
  title: opt(Schema.String),
  why: opt(Schema.String),
  body: opt(Markdown),
  tags: opt(Schema.Array(Slug)),
  paths: opt(Schema.Array(Schema.String)),
  integrations: opt(Schema.Array(IntegrationId)),
  placement: opt(Placement),
  hands: opt(Hands),
  /** Settings wave: who executes it (the preset changes the next attempt only). */
  executor: opt(TaskExecutor),
})

/**
 * Graph commands: the ONLY payloads allowed inside a changeset or a plan_change proposal. Every plan edit — even
 * one drag in the UI — is a changeset, so plan versions, diffs, undo and revert have one uniform unit.
 * Ids are minted by the issuer. Defaults for omitted optional fields are applied in `decide` and written into
 * the event, so `evolve` never invents values.
 */
export const GraphCommand = Schema.TaggedUnion({
  // goals & KPIs
  CreateGoal: {
    goalId: GoalId,
    title: Schema.String,
    why: Schema.String, // not blank: decide rejects it with Invalid (planner rule)
    body: opt(Markdown),
    spec: GoalSpec,
    deadline: opt(Millis),
    kpiIds: opt(Schema.Array(KpiId)),
    rank: opt(Rank),
  },
  UpdateGoal: { goalId: GoalId, patch: GoalPatch },
  SetGoalStatus: {
    goalId: GoalId,
    status: Schema.Literals(["active", "achieved", "missed", "abandoned"]),
    note: Schema.String,
  },
  /** change_kpi_target: targets live on metric goals and bets. */
  ChangeTarget: {
    subject: Schema.TaggedUnion({ goal: { id: GoalId }, initiative: { id: InitiativeId } }),
    condition: MetricCondition,
  },
  DefineKpi: {
    kpiId: KpiId,
    areaId: opt(AreaId),
    key: Slug,
    name: Schema.String,
    unit: Schema.String,
    direction: Schema.Literals(["up", "down"]),
    role: Schema.Literals(["input", "lagging", "health"]),
    source: KpiSource,
    cadence: Schema.Literals(["hourly", "daily", "weekly"]),
    latencyDays: opt(Schema.Int),
    minReportable: opt(Schema.Int),
    rollsUpTo: opt(Schema.Array(KpiId)),
  },
  UpdateKpi: { kpiId: KpiId, patch: KpiPatch },
  // areas & agents
  CreateArea: {
    areaId: AreaId,
    slug: Slug,
    name: Schema.String,
    color: Schema.String,
    charter: Charter,
    policyId: opt(PolicyId),
    placement: opt(PlacementDefaults),
    powers: opt(AreaPowers),
    wip: opt(Schema.Struct({ maxActiveInitiatives: Schema.Int })),
    templateId: opt(Schema.String),
  },
  UpdateArea: { areaId: AreaId, patch: AreaPatch },
  ReviseAreaCharter: { areaId: AreaId, charter: Charter },
  ArchiveArea: { areaId: AreaId },
  /** hire_agent. `policyPreset` selects a default policy document for the role; the policy itself is a human-edited record. */
  HireAgent: {
    agentId: AgentId,
    role: Schema.Literals(["lead", "role"]),
    areaId: Schema.NullOr(AreaId),
    name: Schema.String,
    handle: Handle,
    title: Schema.String,
    charter: Charter,
    mind: Mind,
    hands: opt(Hands),
    policyId: PolicyId,
    policyPreset: Schema.Literals(["lead", "role", "reviewer"]),
  },
  UpdateAgent: { agentId: AgentId, patch: AgentPatch },
  ReviseAgentCharter: { agentId: AgentId, charter: Charter },
  ChangeAgentMind: { agentId: AgentId, mind: Mind },
  SetAgentStatus: { agentId: AgentId, status: Schema.Literals(["active", "paused", "retired"]) },
  /** People only (settings wave K3b): the actor becomes the agent's owner; `mind` replaces the mind in the same step. */
  TakeOverAgent: { agentId: AgentId, mind: opt(Mind) },
  // initiatives
  CreateInitiative: {
    initiativeId: InitiativeId,
    goalIds: Schema.NonEmptyArray(GoalId),
    areaId: AreaId,
    title: Schema.String,
    why: Schema.String, // not blank: decide rejects it with Invalid (planner rule)
    body: opt(Markdown),
    horizon: Horizon,
    shape: InitiativeShape,
    ownerAgentId: opt(AgentId),
    supersedes: opt(InitiativeId),
    rank: opt(Rank),
  },
  UpdateInitiative: { initiativeId: InitiativeId, patch: InitiativePatch },
  StartInitiative: { initiativeId: InitiativeId }, // shaping|paused → active; WIP-checked
  PauseInitiative: { initiativeId: InitiativeId },
  CloseInitiative: {
    initiativeId: InitiativeId,
    as: Schema.Literals(["done", "killed", "abandoned"]),
    reason: Schema.String,
  },
  // tasks
  CreateTask: {
    taskId: TaskId,
    parent: TaskParent,
    title: Schema.String,
    why: Schema.String, // not blank: decide rejects it with Invalid (planner rule)
    body: opt(Markdown),
    kind: opt(Schema.Literals(["work", "publish"])),
    intentKey: IntentKey,
    assigneeId: Schema.NullOr(AgentId),
    /** Omitted = the initiative's area. Another area = a cross-area request (request: pending). */
    areaId: opt(AreaId),
    checks: Schema.Array(DoneCheck),
    review: opt(ReviewConfig),
    placement: opt(Placement),
    hands: opt(Hands),
    tags: opt(Schema.Array(Slug)),
    paths: opt(Schema.Array(Schema.String)),
    integrations: opt(Schema.Array(IntegrationId)),
    supersedes: opt(TaskId),
    rank: opt(Rank),
    /** Settings wave: absent = worker{preset: null} (decide writes it into TaskCreated). */
    executor: opt(TaskExecutor),
  },
  UpdateTask: { taskId: TaskId, patch: TaskPatch },
  /** After the contract froze (first attempt started) this needs a human: for agents it is always `propose`. */
  ReviseContract: { taskId: TaskId, checks: Schema.Array(DoneCheck), review: opt(ReviewConfig) },
  AssignTask: { taskId: TaskId, assigneeId: Schema.NullOr(AgentId) },
  CancelTask: { taskId: TaskId, reason: Schema.String },
  /**
   * Close an open task as done (decision 0032): work that finished outside an accepted run. Evidence is checked against
   * the contract like a worker's done claim; later review stages still run. Capability `complete_task`.
   */
  CompleteTask: { taskId: TaskId, note: Schema.String, evidence: Schema.NonEmptyArray(Evidence) },
  /** The receiving area's intake agent accepts or declines a cross-area request. */
  DecideRequest: { taskId: TaskId, accept: Schema.Boolean, note: opt(Schema.String) },
  Reprioritize: { ref: RankedRef, rank: Rank },
  // edges
  AddEdge: {
    edgeId: EdgeId,
    kind: EdgeKind,
    from: WorkRef,
    to: WorkRef,
    interfaceKey: opt(Slug),
    note: opt(Schema.String),
  },
  RemoveEdge: { edgeId: EdgeId },
})
export type GraphCommand = typeof GraphCommand.Type

/** Commands a chosen question option may run (re-decided at answer time, as the deciding human). */
export const FlowCommand = Schema.TaggedUnion({
  RetryTask: { taskId: TaskId, note: opt(Schema.String) }, // new attempt after a human-needing block; resets the failure fingerprint count
  UnblockTask: { taskId: TaskId, note: Schema.String, hold: opt(Schema.Boolean) }, // hold: also pause it (no new attempt)
  AcceptImpossible: { taskId: TaskId, note: Schema.String },
  PauseTask: { taskId: TaskId, paused: Schema.Boolean, note: opt(Schema.String) },
})
export const OptionEffect = Schema.Union([GraphCommand, FlowCommand])
// #endregion graph-commands
